/** Keep only the most recent unresolved generation per creator/game. Storage
 * failure still permits same-page retry; refresh recovery needs local storage.
 * No provider credentials, files or media bytes are persisted here. */
type Pending = { signature: string; requestId: string };
export function createAssetRequestRecovery(storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  newId = () => crypto.randomUUID()) {
  const memory = new Map<string, Pending>();
  const keyFor = (uid: string, creationId: string) => `djgg.asset-request.v1:${encodeURIComponent(uid)}:${encodeURIComponent(creationId)}`;
  return {
    read(uid: string, creationId: string): unknown {
      const key = keyFor(uid, creationId);
      try {
        const saved = JSON.parse(storage?.getItem(key) ?? 'null') as Pending | null;
        return JSON.parse((saved ?? memory.get(key))?.signature ?? 'null');
      } catch {
        try { return JSON.parse(memory.get(key)?.signature ?? 'null'); } catch { return null; }
      }
    },
    prepare(uid: string, creationId: string, payload: unknown) {
      const key = keyFor(uid, creationId);
      const signature = JSON.stringify(payload, (_key, value) => value && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]])) : value);
      let previous = memory.get(key);
      try {
        const saved = JSON.parse(storage?.getItem(key) ?? 'null') as Pending | null;
        if (saved && typeof saved.signature === 'string' && typeof saved.requestId === 'string' && /^[A-Za-z0-9_-]{16,100}$/.test(saved.requestId)) previous = saved;
      } catch { /* Storage is optional; retain the in-memory intent. */ }
      const pending = previous?.signature === signature ? previous : { signature, requestId: newId() };
      memory.set(key, pending);
      try { storage?.setItem(key, JSON.stringify(pending)); } catch { /* Same-page retries remain safe. */ }
      return { requestId: pending.requestId, complete() {
        if (memory.get(key)?.requestId === pending.requestId) memory.delete(key);
        try {
          const saved = JSON.parse(storage?.getItem(key) ?? 'null') as Pending | null;
          if (saved?.requestId === pending.requestId) storage?.removeItem(key);
        } catch { /* Never erase another tab's newer intent. */ }
      } };
    },
  };
}
